import {
  STATUS_LABELS,
  type SolicitacaoPagamentoDto,
} from "@/services/solicitacaoPagamento";
import { formatCurrency, formatDate, formatDateTime } from "@/utils/format";
import { pixTipoChaveLabel } from "../pagamentoMetodoUtils";

export interface PaymentRequestPdfModel {
  numeroSolicitacao: string;
  status: string;
  dataCriacao: string;
  valor: string;
  vencimento: string;
  tipoDocumento: string;
  planoFinanceiro: string;
  centroCusto: string;
  fornecedor: string;
  solicitante: string;
  formaPagamento: string;
  dadosBancarios: Array<{ label: string; value: string }>;
  aprovadorOperacional: string;
  dataAprovacaoOperacional: string | null;
  observacao: string;
  anexos: string[];
  emitidoEm: string;
  geradoPor: string;
  filenameBase: string;
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80) || "solicitacao-pagamento";
}

function buildDadosBancarios(item: SolicitacaoPagamentoDto): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [
    { label: "Forma de Pagamento", value: item.formaPagamento },
  ];

  if (item.formaPagamento === "PIX") {
    if (item.pixTipoChave) {
      rows.push({ label: "Tipo da Chave", value: pixTipoChaveLabel(item.pixTipoChave) });
    }
    if (item.pixChave) rows.push({ label: "Chave PIX", value: item.pixChave });
    if (item.pixContaEmNome) rows.push({ label: "Conta em nome", value: item.pixContaEmNome });
  } else if (item.formaPagamento === "TRANSFERENCIA") {
    if (item.transferenciaBanco) rows.push({ label: "Banco", value: item.transferenciaBanco });
    if (item.transferenciaAgencia) rows.push({ label: "Agência", value: item.transferenciaAgencia });
    if (item.transferenciaConta) rows.push({ label: "Conta", value: item.transferenciaConta });
    if (item.transferenciaTitular) rows.push({ label: "Favorecido", value: item.transferenciaTitular });
  } else if (item.formaPagamento === "BOLETO" && item.boletoCodigoBarras) {
    rows.push({ label: "Código de barras", value: item.boletoCodigoBarras });
  }

  return rows;
}

function resolveOperationalApproval(item: SolicitacaoPagamentoDto): {
  name: string;
  at: string | null;
} {
  const timelineEvent = item.timeline?.find((e) => e.type === "aprovada_operacional");
  return {
    name: item.reviewedByName?.trim() || timelineEvent?.userName?.trim() || item.aprovadorName?.trim() || "—",
    at: item.reviewedAt || timelineEvent?.at || null,
  };
}

export function buildPaymentRequestPdfModel(
  item: SolicitacaoPagamentoDto,
  geradoPor: string,
): PaymentRequestPdfModel {
  const approval = resolveOperationalApproval(item);
  const numero = item.descricao?.trim() || "Solicitação de pagamento";
  const anexos = item.anexoOriginalName ? [item.anexoOriginalName] : [];

  return {
    numeroSolicitacao: numero,
    status: STATUS_LABELS[item.status] ?? item.status,
    dataCriacao: formatDate(item.createdAt.slice(0, 10)),
    valor: formatCurrency(item.valor),
    vencimento: item.dueDate ? formatDate(item.dueDate) : "—",
    tipoDocumento: item.documentIdentificationId?.trim() || "REC",
    planoFinanceiro: item.paymentCategoryLabel || "—",
    centroCusto: item.costCenterLabel || "—",
    fornecedor: item.creditorName || "—",
    solicitante: item.solicitanteName || "—",
    formaPagamento: item.formaPagamento,
    dadosBancarios: buildDadosBancarios(item),
    aprovadorOperacional: approval.name,
    dataAprovacaoOperacional: approval.at ? formatDateTime(approval.at) : null,
    observacao: item.observacao?.trim() || "—",
    anexos,
    emitidoEm: formatDateTime(new Date().toISOString()),
    geradoPor: geradoPor.trim() || "Usuário",
    filenameBase: sanitizeFilename(numero),
  };
}
